import { Box, Button, Divider, Stack, Typography } from '@mui/material_modern'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import { useAppDispatch, useAppSelector } from 'utils/custom-hooks/useTypedReduxStore.ts'
import React, { useEffect, useMemo } from 'react'
import { selectPaymentRestrictionsBladeProps } from 'state/ui/selectors.ts'
import { ModalBladeProps } from 'components/layouts/ContentLayout.tsx'
import { showFormModal } from 'state/ui/index.ts'
import {
  CONTACT_COMPLIANCE_FIELDS_MAP,
  VEHICLE_COMPLIANCE_FIELDS_MAP,
  PAYMENT_COMPLIANCE_FIELDS_MAP,
} from 'components/containers/bills/bills-constants.ts'

const style = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: '100%',
  height: '100%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  overflow: 'auto',
}

export interface PaymentRestrictionsBladeProps {
  validationErrorPaths: string[]
  customerId: string
}

const categorizeFields = (missingFields: string[]) => {
  const missingContactFields: string[] = []
  const missingVehicleFields: string[] = []
  const missingPaymentFields: string[] = []

  missingFields?.forEach(field => {
    if (CONTACT_COMPLIANCE_FIELDS_MAP[field]) {
      missingContactFields.push(CONTACT_COMPLIANCE_FIELDS_MAP[field])
    } else if (VEHICLE_COMPLIANCE_FIELDS_MAP[field]) {
      missingVehicleFields.push(VEHICLE_COMPLIANCE_FIELDS_MAP[field])
    } else if (PAYMENT_COMPLIANCE_FIELDS_MAP[field]) {
      missingPaymentFields.push(PAYMENT_COMPLIANCE_FIELDS_MAP[field])
    }
  })

  return { missingContactFields, missingVehicleFields, missingPaymentFields }
}

export const PaymentRestrictionsBlade: React.FC<ModalBladeProps> = ({ onHideModal, onFormStateChange }) => {
  const dispatch = useAppDispatch()
  useEffect(() => onFormStateChange(false), [])
  const { validationErrorPaths, customerId } = useAppSelector(selectPaymentRestrictionsBladeProps)

  const { missingContactFields, missingVehicleFields, missingPaymentFields } = useMemo(
    () => categorizeFields(validationErrorPaths),
    [validationErrorPaths],
  )

  const handleEditCustomerInfo = () => {
    onHideModal() // hide the current modal
    dispatch(
      showFormModal({
        formModalName: 'new-customer-modal',
        formModalProps: { customerId },
      }),
    )
  }

  return (
    <Stack height="100vh" sx={style}>
      <FormModalHeading title="Payment Restrictions" backButtonPosition="right" onBack={onHideModal} />
      <Divider />

      <Box px={4} py={2} flexGrow={1}>
        {missingContactFields.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Contact Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>Attention:</strong> The following fields are required for the Customer contact before you can
              continue to payment:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {missingContactFields.map((field, index) => (
                <div key={index}>
                  {index + 1}. {field}
                </div>
              ))}
            </Typography>
          </Box>
        )}

        {missingVehicleFields.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Vehicle Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>Attention:</strong> The following fields are required for the selected customer vehicle before you
              can continue to payment:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {missingVehicleFields.map((field, index) => (
                <div key={index}>
                  {index + 1}. {field}
                </div>
              ))}
            </Typography>
          </Box>
        )}
        {(!!missingContactFields.length || !!missingVehicleFields.length) && (
          <Button sx={{ mt: 2 }} variant="outlined" fullWidth onClick={handleEditCustomerInfo}>
            EDIT CUSTOMER INFORMATION
          </Button>
        )}

        {missingPaymentFields.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Customer Identification Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>Attention:</strong> You need to capture the following information on this Payment before it can be
              paid
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {missingPaymentFields.map((field, index) => (
                <div key={index}>
                  {index + 1}. {field}
                </div>
              ))}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
      </Box>

      <Divider />
    </Stack>
  )
}

export default PaymentRestrictionsBlade
